/**
 * The stored answer key of a static prompt: the answers each field accepts. Kept apart from
 * the editor component so the eagerly loaded module file never pulls the form into the bundle.
 */
import type { StaticPromptDefinition } from "./types";

/** One entry per field: the answers the server will accept for it. */
export type StaticPromptSolution = Record<string, { accepted: string[] }>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function emptySolution(_definition: StaticPromptDefinition): StaticPromptSolution {
  return {};
}

/** Reads whatever is stored, however wrong, and keeps the strings it can use. */
export function acceptedOf(value: unknown, fieldId: string): string[] {
  if (!isRecord(value)) return [];
  const entry = value[fieldId];
  if (!isRecord(entry) || !Array.isArray(entry.accepted)) return [];
  return entry.accepted.filter((item): item is string => typeof item === "string");
}

/** Replaces one field's accepted answers, dropping blanks and empty fields. */
export function withAccepted(
  value: unknown,
  fieldId: string,
  accepted: string[],
): StaticPromptSolution {
  const next: StaticPromptSolution = {
    ...(isRecord(value) ? (value as StaticPromptSolution) : {}),
  };
  const kept = accepted.filter((entry) => entry.trim().length > 0);
  if (kept.length === 0) delete next[fieldId];
  else next[fieldId] = { accepted: kept };
  return next;
}
